/* ------------------------------------------------------------------ */
/*  GA4 bridge for the embedded widget                                 */
/* ------------------------------------------------------------------ */
/*
 * The widget runs inside the host page's JavaScript context (Shadow DOM
 * isolates CSS, not JS), so it can push events into the site's existing
 * GA4 setup: GTM's dataLayer when present, else gtag directly. Events land
 * in the host property with the visitor's client_id and Consent Mode —
 * the widget never sets cookies or IDs itself.
 *
 * Every push is fire-and-forget and must never throw into UI code; on
 * pages with neither dataLayer nor gtag (e.g. the standalone dev page)
 * everything silently no-ops.
 */

import type { Product } from './types';

/** The item_list_id/name carried on ecommerce events — GA4's item-list
 *  attribution uses it to tie add_to_cart/purchase back to the search. */
export const GA4_ITEM_LIST_ID = 'botilia_search';

let enabled = true;

/** Opt out via BotiliaSearchConfig.analytics: false (default on). */
export function configureAnalytics(on: boolean): void {
  enabled = on;
}

export function ga4Event(name: string, params: Record<string, unknown>): void {
  if (!enabled || typeof window === 'undefined') return;
  try {
    const w = window as unknown as {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };
    if (Array.isArray(w.dataLayer)) {
      // GTM route: ecommerce payloads must be cleared between pushes so
      // successive events don't inherit each other's items.
      if (params.ecommerce) w.dataLayer.push({ ecommerce: null });
      w.dataLayer.push({ event: name, ...params });
    } else if (typeof w.gtag === 'function') {
      w.gtag('event', name, params);
    }
  } catch {
    /* analytics must never break search */
  }
}

/** Map a widget product to a GA4 ecommerce item. */
export function ga4Item(product: Product, index?: number): Record<string, unknown> {
  return {
    item_id: product.id,
    item_name: product.name,
    item_category: product.categories?.[0] ?? product.wineType ?? '',
    item_brand: product.producer,
    price: product.salePrice ?? product.price,
    ...(index !== undefined ? { index } : {}),
    item_list_id: GA4_ITEM_LIST_ID,
    item_list_name: 'Botilia Search',
  };
}

/** search + view_item_list (or search_no_results) for a settled page-1 query. */
export function ga4Search(searchTerm: string, resultsCount: number, products: Product[]): void {
  ga4Event('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
  if (resultsCount === 0) {
    ga4Event('search_no_results', { search_term: searchTerm });
    return;
  }
  ga4Event('view_item_list', {
    item_list_id: GA4_ITEM_LIST_ID,
    item_list_name: 'Botilia Search',
    search_term: searchTerm,
    ecommerce: {
      item_list_id: GA4_ITEM_LIST_ID,
      item_list_name: 'Botilia Search',
      items: products.slice(0, 24).map((p, i) => ga4Item(p, i)),
    },
  });
}

/** select_item for a clicked result (position = 0-based index in the list). */
export function ga4SelectItem(product: Product, position?: number): void {
  ga4Event('select_item', {
    item_list_id: GA4_ITEM_LIST_ID,
    item_list_name: 'Botilia Search',
    ecommerce: {
      item_list_id: GA4_ITEM_LIST_ID,
      item_list_name: 'Botilia Search',
      items: [ga4Item(product, position)],
    },
  });
}
